import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { useLang } from '../LangContext';
import { submitLead } from '../lib/api';

// sessionStorage key — UI behaviour flag, **not** sensitive data. Cleared
// when the tab closes. We use sessionStorage (not localStorage) so a user
// who comes back tomorrow gets a fresh chance to convert.
const SS_KEY = 'horren_exit_intent_shown_v1';

// Delay after page load before we arm the listener — gives the user time
// to actually engage with the page before being asked to leave.
const ARM_DELAY_MS = 8000;

const COPY = {
  nl: {
    eyebrow: 'Voor je gaat …',
    headline: 'Krijg een kosteloos offerte-advies binnen 24 uur.',
    sub: 'Laat je naam en e-mail achter — wij sturen je een persoonlijke prijsinschatting voor jouw situatie.',
    name: 'Naam',
    email: 'E-mail',
    submit: 'Offerte aanvragen',
    sending: 'Versturen …',
    success: 'Bedankt — we nemen binnen 24 uur contact op.',
    err: 'Versturen mislukt — probeer het later opnieuw.',
    close: 'Sluiten',
  },
  de: {
    eyebrow: 'Bevor du gehst …',
    headline: 'Kostenlose Preiseinschätzung innerhalb 24 Stunden.',
    sub: 'Hinterlasse Name + E-Mail — wir senden dir eine persönliche Angebots-Skizze für dein Projekt.',
    name: 'Name',
    email: 'E-Mail',
    submit: 'Angebot anfordern',
    sending: 'Wird gesendet …',
    success: 'Danke — wir melden uns innerhalb von 24 Stunden.',
    err: 'Senden fehlgeschlagen — bitte später erneut versuchen.',
    close: 'Schließen',
  },
};

/**
 * ExitIntentPopup — once-per-session slide-up panel triggered when the
 * cursor exits the viewport towards the top (URL bar / tab close).
 *
 * Design choices:
 * - Desktop-only (matchMedia hover:hover + pointer:fine). Mobile users
 *   don't have a "leave intent" gesture and would just see this on
 *   any quick scroll, which is the wrong moment to ask.
 * - Triggered exactly once per session (sessionStorage). Coming back
 *   tomorrow → fresh chance.
 * - Arms `ARM_DELAY_MS` after mount: avoids firing for instant bouncers
 *   who never engaged with the page.
 * - Slide-up from bottom-right corner — visible but not modal-blocking.
 *   ESC and overlay click dismiss without recording the "shown" flag,
 *   so the user can dismiss accidentally without losing the trigger.
 *   Actually wait — that means it would fire again. We DO want to record
 *   dismissal so we don't spam. Confirmed: setting the flag on dismiss.
 */
export default function ExitIntentPopup() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);  // session-flag, once true stays true
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');  // idle | sending | ok | err
  const armedRef = useRef(false);

  const copy = COPY[lang] || COPY.nl;

  // Hydrate the shown-flag from sessionStorage once on mount. If user
  // already saw it this session, we skip arming entirely.
  useEffect(() => {
    try {
      // sessionStorage is not restricted by our ESLint policy — it's the
      // correct primitive for tab-scoped UI state. Cleared automatically
      // when the tab closes; coming back tomorrow → fresh chance.
      if (window.sessionStorage?.getItem(SS_KEY) === '1') setShown(true);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[ExitIntent] sessionStorage read blocked:', e?.message);
      }
    }
  }, []);

  // Arm the mouseout listener — desktop only, after delay, only if not
  // shown this session.
  useEffect(() => {
    if (shown) return undefined;

    // Desktop heuristic: a real pointer that supports hover. Excludes
    // touch-only devices where exit-intent is meaningless.
    const isDesktop = typeof window.matchMedia === 'function'
      ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
      : true;
    if (!isDesktop) return undefined;

    const armTimer = setTimeout(() => { armedRef.current = true; }, ARM_DELAY_MS);

    const onMouseOut = (e) => {
      if (!armedRef.current) return;
      // Only the top edge — cursor moving toward URL/tab bar.
      if (e.clientY > 0) return;
      // Ignore mouseouts between child elements (we only care about
      // leaving the viewport, which has no relatedTarget).
      if (e.relatedTarget || e.toElement) return;
      armedRef.current = false;  // disarm immediately to prevent re-fire
      setOpen(true);
    };

    document.addEventListener('mouseout', onMouseOut);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mouseout', onMouseOut);
    };
  }, [shown]);

  // ESC to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const markShown = () => {
    setShown(true);
    try {
      window.sessionStorage?.setItem(SS_KEY, '1');
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[ExitIntent] sessionStorage write blocked:', e?.message);
      }
    }
  };

  const dismiss = () => {
    setOpen(false);
    markShown();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await submitLead({
        name: name.trim(),
        email: email.trim(),
        language: lang,
        source: 'exit_intent',
        message: 'Exit-intent quick capture (24h Angebots-Anfrage).',
      });
      setStatus('ok');
      markShown();
      // Auto-close after 2.5s so the page is fully usable again.
      setTimeout(() => setOpen(false), 2500);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[ExitIntent] submit failed:', err);
      }
      setStatus('err');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="exit-intent-popup"
          role="dialog" aria-modal="true" aria-labelledby="exit-intent-headline"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 right-4 z-[60] w-[min(420px,calc(100vw-32px))]
                     bg-white rounded-2xl shadow-2xl border border-[#E5E0D2] overflow-hidden"
        >
          <button type="button" onClick={dismiss}
            data-testid="exit-intent-close" aria-label={copy.close}
            className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-full
                       text-[#5C5852] hover:bg-[#FAF8F2] transition-colors">
            <X className="w-4 h-4" />
          </button>

          {status === 'ok' ? (
            <div data-testid="exit-intent-success" className="p-7 pt-9 text-center">
              <span className="inline-flex w-12 h-12 rounded-full bg-[#7FC242]/15 text-[#7FC242] items-center justify-center mb-3">
                <CheckCircle2 className="w-6 h-6" />
              </span>
              <p className="font-display text-[18px] text-[#1F1D1A]">{copy.success}</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="p-6 pt-7 space-y-3">
              <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#F26B1A]">
                {copy.eyebrow}
              </p>
              <h2 id="exit-intent-headline" className="font-display text-[19px] leading-[1.2] text-[#1F1D1A]">
                {copy.headline}
              </h2>
              <p className="text-[12.5px] text-[#5C5852] leading-[1.5]">{copy.sub}</p>

              <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
                placeholder={copy.name} data-testid="exit-intent-name" autoComplete="name"
                className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[13.5px]" />
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={copy.email} data-testid="exit-intent-email" autoComplete="email"
                className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[13.5px]" />

              <button type="submit" disabled={status === 'sending'}
                data-testid="exit-intent-submit"
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full
                           bg-[#F26B1A] text-white text-[13.5px] font-semibold
                           hover:bg-[#D85A0A] disabled:opacity-50 transition-colors">
                {status === 'sending'
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> {copy.sending}</>
                  : <>{copy.submit} <ArrowRight className="w-4 h-4" /></>}
              </button>

              {status === 'err' && (
                <p data-testid="exit-intent-error" className="text-[12px] text-[#C03A1A]">{copy.err}</p>
              )}
            </form>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
